
import { useScrollReveal } from '../../../hooks/useScrollReveal';

export default function Relationship() {
  const headerReveal = useScrollReveal({ threshold: 0.2 });
  const card1Reveal = useScrollReveal({ threshold: 0.2 });
  const card2Reveal = useScrollReveal({ threshold: 0.2 });
  const card3Reveal = useScrollReveal({ threshold: 0.2 });
  const card4Reveal = useScrollReveal({ threshold: 0.2 });
  const highlightReveal = useScrollReveal({ threshold: 0.2 });

  return (
    <section className="py-32 bg-gradient-to-br from-purple-50 via-white to-pink-50 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-20 right-10 w-64 h-64 bg-purple-200/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-pink-200/30 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div 
          ref={headerReveal.ref}
          className={`text-center mb-20 ${headerReveal.isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
        >
          <div className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
            💝 RELATIONSHIPS
          </div>
          <h2 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 mb-6">
            How This Book Will Help Your Relationships
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Understanding yourself is the first step towards deeper and more authentic relationships with others
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {/* Card 1 */}
          <div 
            ref={card1Reveal.ref}
            className={`group bg-white rounded-2xl p-8 shadow-lg hover-tilt hover-lift relative overflow-hidden ${
              card1Reveal.isVisible ? 'animate-fade-in-up stagger-1' : 'opacity-0'
            }`}
          >
            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Icon with animated gradient */}
            <div className="relative mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-300 animate-gradient">
                <i className="ri-heart-pulse-line text-3xl text-white"></i>
              </div>
              {/* Glow effect */}
              <div className="absolute inset-0 w-16 h-16 bg-purple-500/30 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-4 relative">
              Why Your Partner Reacts That Way
            </h3>
            <p className="text-gray-600 leading-relaxed relative text-sm">
              You'll understand the reasons for their reactions and behavior patterns that previously seemed illogical.
            </p>

            {/* Hover shimmer effect */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
          </div>

          {/* Card 2 */}
          <div 
            ref={card2Reveal.ref}
            className={`group bg-white rounded-2xl p-8 shadow-lg hover-tilt hover-lift relative overflow-hidden ${
              card2Reveal.isVisible ? 'animate-fade-in-up stagger-2' : 'opacity-0'
            }`}
          >
            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Icon with animated gradient */}
            <div className="relative mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-300 animate-gradient">
                <i className="ri-compass-3-line text-3xl text-white"></i>
              </div>
              {/* Glow effect */}
              <div className="absolute inset-0 w-16 h-16 bg-purple-500/30 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-4 relative">
              What They Need Most
            </h3>
            <p className="text-gray-600 leading-relaxed relative text-sm">
              You'll recognize which energies (Fire, Air, Water, or Earth) they lack and how it affects your relationship.
            </p>

            {/* Hover shimmer effect */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
          </div>

          {/* Card 3 */}
          <div 
            ref={card3Reveal.ref}
            className={`group bg-white rounded-2xl p-8 shadow-lg hover-tilt hover-lift relative overflow-hidden ${
              card3Reveal.isVisible ? 'animate-fade-in-up stagger-3' : 'opacity-0'
            }`}
          >
            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Icon with animated gradient */}
            <div className="relative mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-300 animate-gradient">
                <i className="ri-hand-heart-line text-3xl text-white"></i>
              </div>
              {/* Glow effect */}
              <div className="absolute inset-0 w-16 h-16 bg-purple-500/30 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-4 relative">
              How to Support Them
            </h3>
            <p className="text-gray-600 leading-relaxed relative text-sm">
              You'll learn ways to create a safe space where your partner feels understood and can open up.
            </p>

            {/* Hover shimmer effect */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
          </div>

          {/* Card 4 */}
          <div 
            ref={card4Reveal.ref}
            className={`group bg-white rounded-2xl p-8 shadow-lg hover-tilt hover-lift relative overflow-hidden ${
              card4Reveal.isVisible ? 'animate-fade-in-up stagger-4' : 'opacity-0'
            }`}
          >
            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Icon with animated gradient */}
            <div className="relative mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-300 animate-gradient">
                <i className="ri-chat-heart-line text-3xl text-white"></i>
              </div>
              {/* Glow effect */}
              <div className="absolute inset-0 w-16 h-16 bg-purple-500/30 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-4 relative">
              What Words Work Best
            </h3>
            <p className="text-gray-600 leading-relaxed relative text-sm">
              You'll discover which communication works best, and which words create distance or misunderstanding.
            </p>

            {/* Hover shimmer effect */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
          </div>
        </div>

        {/* Bottom Highlight Section */}
        <div 
          ref={highlightReveal.ref}
          className={`relative ${highlightReveal.isVisible ? 'animate-scale-in' : 'opacity-0'}`}
        >
          <div className="bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 rounded-3xl p-12 text-center text-white shadow-2xl relative overflow-hidden">
            {/* Animated sparkle effects */}
            <div className="absolute top-10 left-10 w-3 h-3 bg-white rounded-full animate-sparkle"></div>
            <div className="absolute top-20 right-20 w-2 h-2 bg-white rounded-full animate-sparkle" style={{ animationDelay: '0.5s' }}></div>
            <div className="absolute bottom-10 left-1/4 w-2 h-2 bg-white rounded-full animate-sparkle" style={{ animationDelay: '1s' }}></div>
            <div className="absolute bottom-20 right-1/3 w-3 h-3 bg-white rounded-full animate-sparkle" style={{ animationDelay: '1.5s' }}></div>

            {/* Shimmer overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>

            <div className="relative z-10">
              <div className="inline-block bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-semibold mb-6">
                ✨ KEY INSIGHT
              </div>
              <h3 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                Relationships Start With Yourself
              </h3>
              <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                You can't expect healthy relationships with others if you don't have a healthy relationship with yourself. 
                This book will help you start that journey.
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes sparkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-sparkle {
          animation: sparkle 2s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}
